import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash-es/debounce';
import { postAddressAutocomplete } from 'app/apis/address';
import { type CartShippingSlice } from 'app/store/cart';
import { ROLE } from 'app/cart/constants';

interface UseAutocompleteProps {
    enable: boolean;
    isBuyer: boolean;
    onChange: CartShippingSlice['partialUpdateAddressForm'];
    shipTo: string | null;
    shippingType: ShippingType | null;
    zipCode: string | null;
    county: string | null;
    district: string | null;
    road: string | null;
    address: string | null;
    latestUpdateField: CartShippingSlice['latestUpdateField'];
    allowedAutoCompleteFields: ReturnType<typeof getAllowedAutoCompleteFields>;
    updateLatestUpdateField: CartShippingSlice['updateLatestUpdateField'];
}

export function getAllowedAutoCompleteFields(
    role: ROLE,
    addressForm: CartShippingSlice['addressForm']
) {
    return addressForm[role]
        .filter(({ field }) => field.auto_complete)
        .map((field) => field.key);
}

export function useAutocomplete({
    enable,
    isBuyer,
    onChange,
    shipTo,
    shippingType,
    zipCode,
    county,
    district,
    road,
    latestUpdateField,
    allowedAutoCompleteFields,
    address,
    updateLatestUpdateField,
}: UseAutocompleteProps) {
    const role = useRef(isBuyer ? ROLE.BUYER : ROLE.RECEIVER).current;

    const [readyToAutocomplete, setReadyToAutocomplete] = useState(false);

    const cachedLatestUpdateFieldRef = useRef('');

    const latestPayloadRef = useRef<
        Omit<PostCartAddressAutocompletePayload, 'role' | 'latest_update_field'>
    >({
        ship_to: shipTo,
        shipping_type: shippingType,
        address_field_zip_code: zipCode,
        address_field_county: county,
        address_field_district: district,
        address_field_road: road,
        address_field_address: address,
    });

    const debouncedPostAddressAutocomplete = useRef(
        debounce(async (payload) => {
            try {
                const resp = await postAddressAutocomplete(payload);

                if (resp?.data) {
                    onChange(role, resp?.data);

                    const { role: _, latest_update_field, ...rest } = payload;

                    latestPayloadRef.current = rest;

                    updateLatestUpdateField([role, latest_update_field]);

                    cachedLatestUpdateFieldRef.current = latest_update_field;
                }
            } catch (error) {
                console.error(error);
            }

            setReadyToAutocomplete(true);
        }, 300)
    ).current;

    useEffect(() => {
        if (!enable) {
            setReadyToAutocomplete(false);

            return;
        }

        if (shipTo && shippingType) {
            if (
                (latestUpdateField[0] !== role ||
                    (latestUpdateField[1] &&
                        !allowedAutoCompleteFields.includes(latestUpdateField[1])) ||
                    cachedLatestUpdateFieldRef.current === latestUpdateField[1]) &&
                readyToAutocomplete
            ) {
                cachedLatestUpdateFieldRef.current = '';

                return;
            }

            debouncedPostAddressAutocomplete({
                role,
                ship_to: shipTo,
                shipping_type: shippingType,
                address_field_zip_code: zipCode,
                address_field_county: county,
                address_field_district: district,
                address_field_road: road,
                latest_update_field: latestUpdateField[1] || '',
                address_field_address: address,
            });
        }
    }, [
        shipTo,
        shippingType,
        zipCode,
        county,
        district,
        road,
        onChange,
        role,
        enable,
        address,
        debouncedPostAddressAutocomplete,
        latestUpdateField,
        allowedAutoCompleteFields,
        readyToAutocomplete,
    ]);
}
